import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Scale,
  Check,
  Loader2,
  CreditCard,
  Zap,
  Building,
  Shield,
  Sparkles,
} from 'lucide-react'
import { paymentsApi, authApi, Plan } from '../services/api'
import { useOnboardingStatus } from '../hooks/useOnboardingStatus'

export default function OnboardingPlanSelectionPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { exists, requiresPlanSelection, isLoading: statusLoading } = useOnboardingStatus()
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
    'yearly'
  )

  // Redirect based on onboarding status
  useEffect(() => {
    if (!statusLoading) {
      if (!exists) {
        // User not registered, go to onboarding
        navigate('/onboarding', { replace: true })
      } else if (!requiresPlanSelection) {
        // User already has a plan, go to main app
        navigate('/', { replace: true })
      }
    }
  }, [statusLoading, exists, requiresPlanSelection, navigate])
  const [loadingPlanTrial, setLoadingPlanTrial] = useState<string | null>(null)
  const [loadingPlanPay, setLoadingPlanPay] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: plans, isLoading } = useQuery({
    queryKey: ['plans'],
    queryFn: () => paymentsApi.getPlans().then((r) => r.data),
  })

  const selectPlanMutation = useMutation({
    mutationFn: (data: { planId: string; startTrial: boolean }) =>
      authApi.selectPlan(data),
    onSuccess: (response) => {
      if (response.data.status === 'trial_started') {
        // Invalidate onboarding status and redirect to main app
        queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
        navigate('/', { replace: true })
      } else if (response.data.redirectTo) {
        // Redirect to payment/pricing
        navigate(response.data.redirectTo, { replace: true })
      }
    },
    onError: () => {
      setError('Nie udalo sie wybrac planu. Sprobuj ponownie.')
      setLoadingPlanTrial(null)
      setLoadingPlanPay(null)
    },
  })

  const createOrderMutation = useMutation({
    mutationFn: (data: { planId: string; billingPeriod: 'monthly' | 'yearly' }) =>
      paymentsApi.createOrder(data),
    onSuccess: (response) => {
      // Redirect to payment page
      window.location.href = response.data.paymentUrl
    },
    onError: () => {
      setError('Nie udalo sie utworzyc zamowienia. Sprobuj ponownie.')
      setLoadingPlanPay(null)
    },
  })

  const handleStartTrial = (planId: string) => {
    setLoadingPlanTrial(planId)
    setError(null)
    selectPlanMutation.mutate({ planId, startTrial: true })
  }

  const handlePayNow = (planId: string) => {
    setLoadingPlanPay(planId)
    setError(null)
    createOrderMutation.mutate({ planId, billingPeriod })
  }

  const getPrice = (plan: Plan) => {
    if (billingPeriod === 'yearly' && plan.priceYearly) {
      return plan.priceYearly
    }
    return plan.priceMonthly
  }

  const getMonthlyEquivalent = (plan: Plan) => {
    if (billingPeriod === 'yearly' && plan.priceYearly) {
      return (plan.priceYearly / 12).toFixed(0)
    }
    return plan.priceMonthly
  }

  const getSavings = (plan: Plan) => {
    if (billingPeriod === 'yearly' && plan.priceYearly) {
      const yearlyTotal = plan.priceMonthly * 12
      const savings = yearlyTotal - plan.priceYearly
      return savings > 0 ? savings : 0
    }
    return 0
  }

  const parseFeatures = (featuresJson: string | null | undefined): string[] => {
    if (!featuresJson) return []
    try {
      return JSON.parse(featuresJson)
    } catch {
      return []
    }
  }

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'basic':
        return <Zap className="w-6 h-6" />
      case 'extended':
        return <Building className="w-6 h-6" />
      case 'advanced':
        return <Shield className="w-6 h-6" />
      default:
        return <CreditCard className="w-6 h-6" />
    }
  }

  const getPlanColor = (planId: string) => {
    switch (planId) {
      case 'basic':
        return 'blue'
      case 'extended':
        return 'purple'
      case 'advanced':
        return 'orange'
      default:
        return 'gray'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Scale className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Wybierz plan dla swojej kancelarii
          </h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Zacznij od 7-dniowego okresu probnego lub zakup plan od razu.
            Wszystkie funkcje dostepne od pierwszego dnia.
          </p>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white">
              <Check className="w-4 h-4" />
            </div>
            <span className="text-sm text-gray-500">Dane organizacji</span>
          </div>
          <div className="w-8 h-px bg-gray-300" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
              2
            </div>
            <span className="text-sm font-medium text-gray-900">
              Wybor planu
            </span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="max-w-md mx-auto mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              &times;
            </button>
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 inline-flex shadow-sm">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Miesiecznie
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                billingPeriod === 'yearly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Rocznie
              <span className="ml-2 bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">
                -17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans?.map((plan) => {
            const color = getPlanColor(plan.id)
            const isPopular = plan.id === 'extended'
            const features = parseFeatures(plan.features)
            const savings = getSavings(plan)
            const isLoadingTrial = loadingPlanTrial === plan.id
            const isLoadingPay = loadingPlanPay === plan.id
            const isAnyLoading = loadingPlanTrial !== null || loadingPlanPay !== null

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-2xl shadow-lg relative ${
                  isPopular ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Najpopularniejszy
                    </span>
                  </div>
                )}

                <div className="p-6">
                  {/* Plan header */}
                  <div className="flex items-center mb-4">
                    <div className={`p-2 bg-${color}-100 rounded-lg`}>
                      {getPlanIcon(plan.id)}
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {plan.name}
                      </h3>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold text-gray-900">
                        {getMonthlyEquivalent(plan)}
                      </span>
                      <span className="text-gray-500 ml-2">zl/mies.</span>
                    </div>
                    {billingPeriod === 'yearly' && (
                      <p className="text-sm text-gray-500 mt-1">
                        {getPrice(plan)} zl rocznie
                        {savings > 0 && (
                          <span className="text-green-600 ml-1">
                            (oszczedzasz {savings} zl)
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  {/* Description */}
                  {plan.description && (
                    <p className="text-sm text-gray-600 mb-6">
                      {plan.description}
                    </p>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>Do {plan.maxUsers} uzytkownikow</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>{plan.maxDocumentsPerPeriod} dokumentow/mies.</span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>
                        {plan.maxPagesPerPeriod.toLocaleString()} stron/mies.
                      </span>
                    </li>
                    <li className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      <span>
                        {(plan.aiTokensMonthly / 1000).toFixed(0)}k tokenow AI
                      </span>
                    </li>
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    {/* Start Trial button */}
                    <button
                      onClick={() => handleStartTrial(plan.id)}
                      disabled={isAnyLoading}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                        isPopular
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isLoadingTrial ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Uruchamianie...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Rozpocznij 7-dniowy okres probny
                        </>
                      )}
                    </button>

                    {/* Pay now button */}
                    <button
                      onClick={() => handlePayNow(plan.id)}
                      disabled={isAnyLoading}
                      className="w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingPay ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Przetwarzanie...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Kup teraz
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer note */}
        <div className="text-center mt-10">
          <p className="text-sm text-gray-500">
            Okres probny nie wymaga karty platniczej. Mozesz anulowac w dowolnym
            momencie.
          </p>
        </div>
      </div>
    </div>
  )
}
